import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { CommonAppDataService } from '@CaseOne/Common/app_data/app_data.service';
import { CommonUserDataService } from '@CaseOne/Common/user/user_data.service';
import { CommonLocaleService } from '@CaseOne/Common/locale/locale.service';
import { CommonServerDateService } from '@CaseOne/Common/server_date/server_date.service';
import { CommonTextfieldInputMaskAliasesService } from '@CaseOne/Common/controls/textfield/inputmasks_aliases_service';
import { CommonAppRunService } from '@CaseOne/Common/app/common-app-run.service';
import { CommonGeneralSettingsService } from '@CaseOne/Common/general_settings/general_settings.service';
import { CommonNotificationService } from '@CaseOne/Common/notification/notification.service';
import { ICommonAppConfig } from '@CaseOne/Common/app/app.config.token';
import * as FontFaceObserver from 'fontfaceobserver';

declare const COMPONENT_SPECS_MODE: boolean;

@Injectable({
	providedIn: 'root',
})
export class CommonAppInitializersService {
	private initialized$ = new BehaviorSubject<boolean>(false);
	private initPromise: Promise<any> | null = null;
	private additionalProcesses: Promise<any>[] = [];
	private additionalProcessesCompleted = 0;

	public get isInitialized$(): Observable<boolean> {
		return this.initialized$.asObservable();
	}

	public get isInitialized(): boolean {
		return this.initialized$.value;
	}

	/**
	 * FIXME: Remove after migrating to the native Angular Router.
	 * Currently used to load required data before the UI is rendered.
	 */
	public registerAdditionalProcess(process: Promise<any> | any): void {
		if (this.initialized$.value) {
			return;
		}

		const promiseToRegister = process && typeof process.toPromise === 'function'
			? process.toPromise()
			: Promise.resolve(process);

		this.additionalProcesses.push(promiseToRegister);

		promiseToRegister
			.then(() => this.checkAllProcessesCompleted())
			.catch((error) => {
				this.checkAllProcessesCompleted();
			});
	}

	private checkAllProcessesCompleted(): void {
		this.additionalProcessesCompleted++;

		if (this.additionalProcessesCompleted >= this.additionalProcesses.length && this.initPromise) {
			this.initPromise.then(() => {
				if (!this.initialized$.value) {
					this.initialized$.next(true);
				}
			});
		}
	}

	initAll(
		commonAppDataService: CommonAppDataService,
		commonUserDataService: CommonUserDataService,
		commonLocaleService: CommonLocaleService,
		commonServerDateService: CommonServerDateService,
		commonTextfieldInputMaskAliasesService: CommonTextfieldInputMaskAliasesService,
		commonAppRunService: CommonAppRunService,
		commonGeneralSettingsService: CommonGeneralSettingsService,
		commonNotificationService: CommonNotificationService,
		appConfig: ICommonAppConfig,
	): Promise<any[]> {
		if (this.initPromise) {
			return this.initPromise;
		}

		const promises: Promise<any>[] = [];

		promises.push(commonAppDataService.init().toPromise());
		promises.push(commonUserDataService.wait().toPromise());

		if (!COMPONENT_SPECS_MODE) {
			promises.push(commonLocaleService.init());
			promises.push(commonServerDateService.init());
			promises.push(commonTextfieldInputMaskAliasesService.init());
		}

		commonAppRunService.init();

		promises.push(commonNotificationService.init());

		if (!COMPONENT_SPECS_MODE) {
			const FONT_LOADING_TIMEOUT = 60000;
			const fontPromise = Promise.all([
				new FontFaceObserver('Roboto', { weight: 400 }).load(null, FONT_LOADING_TIMEOUT),
				new FontFaceObserver('Roboto', { weight: 500 }).load(null, FONT_LOADING_TIMEOUT),
				new FontFaceObserver('Roboto', { weight: 700 }).load(null, FONT_LOADING_TIMEOUT),
			]);
			promises.push(fontPromise);
		}

		this.initPromise = Promise.all(promises).then((results) => {
			setTimeout(() => {
				if (this.additionalProcesses.length === 0) {
					this.initialized$.next(true);
				}
			}, 100);
			return results;
		});

		return this.initPromise;
	}
}
